import { useState, useEffect, useCallback } from 'react';
import {
  List,
  Show,
  SimpleShowLayout,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  BooleanField,
  TextInput,
  NumberInput,
  DateInput,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  ReferenceField,
  useNotify,
  useRefresh,
  useRecordContext,
  useListContext,
} from 'react-admin';
import { JsonField } from 'react-admin-json-view';
import {
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@material-ui/core';

import qs from 'qs';

import axios from 'utils/axios';
import { defaultPerPage } from 'utils/helpers';
import Pagination from 'components/Pagination';
import {
  paymentChargeStatusChoices,
  paymentFlowChoices,
  reconciledChoices,
} from 'choices';

const STATUS_COLORS = {
  pending: { backgroundColor: '#9e9e9e', color: 'white' },
  retrying: { backgroundColor: '#ff9800', color: 'white' },
  succeeded: { backgroundColor: '#4caf50', color: 'white' },
  failed: { backgroundColor: '#f44336', color: 'white' },
  written_off: { backgroundColor: '#607d8b', color: 'white' },
  refunded: { backgroundColor: '#3f51b5', color: 'white' },
};

const StatusChipField = ({ source = 'status' }) => {
  const record = useRecordContext();
  const value = record?.[source];
  if (!value) return null;

  return (
    <Chip
      label={value.replace(/_/g, ' ')}
      size="small"
      style={STATUS_COLORS[value] || { backgroundColor: '#9e9e9e', color: 'white' }}
    />
  );
};

const AmountField = ({ source = 'amount' }) => {
  const record = useRecordContext();
  if (record?.[source] == null) return null;
  return <span>${record[source]}</span>;
};

const ReconciledField = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Chip
      label={record.reconciled ? 'Reconciled' : 'Open'}
      size="small"
      style={
        record.reconciled
          ? { backgroundColor: '#4caf50', color: 'white' }
          : { backgroundColor: '#eeeeee', color: '#555' }
      }
    />
  );
};

// Custom filters are routed top-level (the dataProvider spreads `topLevel`
// onto the query string) because the backend reads them as top-level params,
// not nested under filter[...]. The free-text `q` stays nested for FilterScope.
const listFilters = [
  <TextInput label="Search (buyer / NMI id)" source="q" alwaysOn />,
  <SelectInput label="Status" source="topLevel.status" choices={paymentChargeStatusChoices} alwaysOn />,
  <SelectInput label="Reconciled" source="topLevel.reconciled" choices={reconciledChoices} alwaysOn />,
  <SelectInput label="Flow" source="topLevel.payment_flow" choices={paymentFlowChoices} />,
  <ReferenceInput
    source="topLevel.buyer_id"
    reference="users"
    label="Buyer"
    filterToQuery={searchText => ({ q: searchText })}
  >
    <AutocompleteInput
      fullWidth
      optionText={record =>
        record?.display_name ? `${record?.display_name} <${record?.email || record?.phone}>` : ''
      }
    />
  </ReferenceInput>,
  <DateInput label="From" source="topLevel.from" />,
  <DateInput label="To" source="topLevel.to" />,
  <NumberInput label="Min amount (cents)" source="topLevel.min_amount_cents" />,
  <NumberInput label="Max amount (cents)" source="topLevel.max_amount_cents" />,
];

const formatMoney = cents => `$${((cents || 0) / 100).toFixed(2)}`;

// Aggregate totals over the *currently active filters* — the reconciliation
// instrument. Refetches when the list filters change.
const ReconciliationSummary = () => {
  const { filterValues } = useListContext();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const filterKey = JSON.stringify(filterValues);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { topLevel = {}, q } = filterValues || {};
      const query = { ...topLevel };
      // Nest `q` under filter[q] and serialize with brackets to match the
      // dataProvider — axios' default serializer would emit filter={"q":..}
      // which the backend ignores, silently dropping the free-text filter.
      if (q) query.filter = { q };
      const { data } = await axios.get(
        `/v1/payment_charges/summary?${qs.stringify(query, { arrayFormat: 'brackets' })}`
      );
      setSummary(data);
    } catch (e) {
      setSummary(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && !summary) {
    return (
      <Card style={{ margin: '8px 0' }}>
        <CardContent>
          <CircularProgress size={18} />
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  const cell = (label, value) => (
    <div key={label} style={{ marginRight: 28 }}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </div>
  );

  return (
    <Card style={{ margin: '8px 0' }}>
      <CardContent style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {cell('Charges', summary.total_count)}
        {cell('Total amount', formatMoney(summary.total_amount_cents))}
        {cell('Reconciled', summary.reconciled_count)}
        {cell('Unreconciled', summary.unreconciled_count)}
        <div style={{ flexBasis: '100%', height: 8 }} />
        {Object.entries(summary.by_status || {}).map(([status, count]) =>
          cell(
            status.replace(/_/g, ' '),
            `${count} · ${formatMoney((summary.amount_by_status || {})[status])}`
          )
        )}
      </CardContent>
    </Card>
  );
};

const ChargeActionButton = ({
  label,
  variant = 'outlined',
  color = 'primary',
  endpoint,
  confirm,
  body,
  successMessage,
  visible = () => true,
  size = 'small',
  style,
  onClick,
}) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  if (!record || !visible(record)) return null;

  const handleClick = async e => {
    e.preventDefault();
    e.stopPropagation();

    // Custom handler (e.g. opens a dialog) — skips the default POST flow.
    if (onClick) return onClick();

    if (confirm && !window.confirm(confirm)) return;

    const payload = typeof body === 'function' ? body(record) : body;
    if (payload === false) return; // body fn can cancel (e.g. prompt dismissed)

    setLoading(true);
    try {
      const { data } = await axios.post(`/v1/payment_charges/${record.id}/${endpoint}`, payload || {});

      if (data?.success === false) {
        const message = (data.errors || []).join(', ') || `Failed to ${label.toLowerCase()}`;
        notify(message, { type: 'error' });
      } else {
        notify(successMessage || `${label} succeeded`, { type: 'success' });
        refresh();
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      const message = Array.isArray(errors)
        ? errors.join(', ')
        : err.response?.data?.error || `Failed to ${(label || 'action').toLowerCase()}`;
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      onClick={handleClick}
      disabled={loading}
      style={{ marginRight: 6, ...style }}
    >
      {loading ? <CircularProgress size={14} /> : label}
    </Button>
  );
};

const RetryButton = props => (
  <ChargeActionButton
    label="Force retry"
    endpoint="retry"
    successMessage="Charge retry queued"
    // This fires a REAL merchant-initiated charge on the buyer's card — confirm
    // first, and only allow it where a prior attempt exists (retrying/failed),
    // never on a brand-new pending charge that hasn't been attempted yet.
    confirm="Force a new charge attempt on the buyer's card now?"
    visible={record => ['retrying', 'failed'].includes(record.status)}
    {...props}
  />
);

const WRITE_OFF_FUNDING_OPTIONS = [
  {
    value: 'platform',
    label: 'Platform covers it',
    helper: 'Debits the platform wallet; seller is paid from platform funds.',
  },
  {
    value: 'external',
    label: 'Settled externally',
    helper: 'Buyer paid us directly outside the platform; seller is still paid, no platform wallet debit.',
  },
];

const WriteOffDialog = ({ record, open, onClose }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  const [funding, setFunding] = useState('platform');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setReason('');
    setFunding('platform');
    onClose();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();

    // A reason is mandatory for the audit trail — the backend also rejects a
    // blank reason, so this is the friendly first line of defence.
    if (!reason.trim()) {
      notify('A write-off reason is required.', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`/v1/payment_charges/${record.id}/write_off`, {
        reason: reason.trim(),
        funding,
      });

      if (data?.success === false) {
        const message = (data.errors || []).join(', ') || 'Failed to write off';
        notify(message, { type: 'error' });
      } else {
        notify('Charge written off', { type: 'success' });
        refresh();
        handleClose();
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      const message = Array.isArray(errors)
        ? errors.join(', ')
        : err.response?.data?.error || 'Failed to write off';
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Write off charge</DialogTitle>

      <DialogContent>
        <DialogContentText>
          The seller will still be paid and this charge closed — this cannot be undone.
        </DialogContentText>

        <MuiTextField
          autoFocus
          required
          fullWidth
          variant="outlined"
          label="Reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          helperText="Required — recorded on the charge for the audit trail."
        />

        <FormControl component="fieldset" required style={{ marginTop: 16 }}>
          <FormLabel component="legend">Funding</FormLabel>
          <RadioGroup value={funding} onChange={e => setFunding(e.target.value)}>
            {WRITE_OFF_FUNDING_OPTIONS.map(option => (
              <div key={option.value}>
                <FormControlLabel
                  value={option.value}
                  control={<Radio color="primary" />}
                  label={option.label}
                />
                <FormHelperText style={{ marginTop: -8, marginLeft: 32 }}>
                  {option.helper}
                </FormHelperText>
              </div>
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="secondary"
          variant="contained"
          disabled={loading || !reason.trim()}
        >
          {loading ? 'Writing off…' : 'Write off'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WriteOffButton = props => {
  const record = useRecordContext();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <ChargeActionButton
        label="Write off"
        endpoint="write_off"
        color="secondary"
        // Only an uncollected charge (failed, or still retrying — e.g. the
        // buyer settled directly with the company mid-retry) may be written
        // off. Succeeded/pending charges are refused: that money was already
        // collected or was never attempted.
        visible={r => ['failed', 'retrying'].includes(r.status)}
        onClick={() => setDialogOpen(true)}
        {...props}
      />
      {record && (
        <WriteOffDialog record={record} open={dialogOpen} onClose={() => setDialogOpen(false)} />
      )}
    </>
  );
};

const RefundButton = props => (
  <ChargeActionButton
    label="Refund"
    endpoint="refund"
    color="secondary"
    confirm="Refund this charge to the buyer?"
    successMessage="Charge refunded"
    visible={record => record.status === 'succeeded'}
    {...props}
  />
);

// Toggle reconciled state; prompt for a note when marking reconciled.
const ReconcileToggle = props => {
  const record = useRecordContext();
  if (!record) return null;

  if (record.reconciled) {
    return (
      <ChargeActionButton
        label="Un-reconcile"
        endpoint="reconcile"
        color="primary"
        confirm="Mark this charge as not reconciled?"
        body={{ reconciled: false }}
        successMessage="Marked as not reconciled"
        {...props}
      />
    );
  }

  return (
    <ChargeActionButton
      label="Reconcile"
      endpoint="reconcile"
      color="primary"
      successMessage="Charge reconciled"
      body={() => {
        const note = window.prompt('Reconciliation note (e.g. settlement reference):', '');
        if (note === null) return false; // cancelled
        return { reconciled: true, note };
      }}
      {...props}
    />
  );
};

// One source of truth for how charge action buttons look, so RETRY / WRITE OFF /
// RECONCILE can never drift apart (variant, size, spacing).
const rowActionProps = { variant: 'outlined', size: 'small' };
const showActionProps = { variant: 'contained', size: 'medium' };

const RowActions = () => (
  <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
    <RetryButton {...rowActionProps} />
    <WriteOffButton {...rowActionProps} />
    <RefundButton {...rowActionProps} />
    <ReconcileToggle {...rowActionProps} />
  </div>
);

export const PaymentChargeList = props => (
  <List
    {...props}
    perPage={defaultPerPage}
    pagination={<Pagination />}
    filters={listFilters}
    aside={<ReconciliationSummary />}
    bulkActionButtons={false}
    sort={{ field: 'created_at', order: 'DESC' }}
  >
    <Datagrid rowClick="show">
      <TextField source="id" />
      <TextField source="buyer_name" label="Buyer" />
      <TextField source="gig_title" label="Gig" />
      <AmountField source="amount" label="Amount" />
      <StatusChipField source="status" label="Status" />
      <TextField source="payment_flow" label="Flow" />
      <ReconciledField label="Reconciled" />
      <NumberField source="attempts" />
      <TextField source="last_error" label="Last error" />
      <DateField source="created_at" showTime />
      <RowActions label="Actions" />
    </Datagrid>
  </List>
);

const ShowActions = () => (
  <div style={{ display: 'flex', alignItems: 'center', padding: 16 }}>
    <RetryButton {...showActionProps} />
    <WriteOffButton {...showActionProps} />
    <RefundButton {...showActionProps} />
    <ReconcileToggle {...showActionProps} />
  </div>
);

export const PaymentChargeShow = props => (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="id" />
      <StatusChipField source="status" label="Status" />
      <AmountField source="amount" label="Amount" />
      <TextField source="payment_flow" label="Flow" />
      <BooleanField source="reconciled" label="Reconciled" />
      <DateField source="reconciled_at" showTime />
      <TextField source="reconciled_by_name" label="Reconciled by" />
      <TextField source="reconciliation_note" label="Reconciliation note" />
      <NumberField source="attempts" />
      <TextField source="last_error" />
      <DateField source="next_retry_at" showTime />
      <DateField source="created_at" showTime />
      <DateField source="written_off_at" showTime />
      <TextField source="written_off_by_id" label="Written off by (admin id)" />
      <TextField source="nmi_transaction_id" label="NMI Transaction ID" />
      <TextField source="payment_id" />
      <TextField source="milestone_id" />
      <ReferenceField source="buyer_id" reference="users" label="Buyer" link="edit">
        <TextField source="display_name" />
      </ReferenceField>
      <TextField source="buyer_email" />
      <TextField source="gig_id" />
      <TextField source="gig_title" />
      <JsonField
        source="response_data"
        reactJsonOptions={{ collapsed: false, displayDataTypes: false }}
      />
      <ShowActions />
    </SimpleShowLayout>
  </Show>
);
